import {
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { Accordion } from "../../../../components/Accordion";
import { FC, useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { usePasswordChange } from "../../../../auth/usePasswordChange";
import { useGetOperationsFactor } from "../../../../auth/useGetOperationsFactor";
import { useSendOperationsFactor } from "../../../../auth/useSendOperationsFactor";
import { TUserInfo } from "../../../verificationRequest/models";
import { PasswordChange } from "../../../../components/PasswordChange";

type Step = "initial" | "error" | "password_form" | "success";

export const PasswordChangeBlock: FC<{ userInfo: TUserInfo }> = ({
  userInfo,
}) => {
  const [step, setStep] = useState<Step>("initial");
  const [sentCode, setSentCode] = useState(false);
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [validationErrors, setValidationError] = useState({} as any);
  const [isPasswordReady, setIsPasswordReady] = useState(false);

  const [
    factor,
    isLoadingOperationsFactor,
    errorGetOperationsFactor,
    getOperationsFactor,
  ] = useGetOperationsFactor();
  const [
    sendOperationsFactorResult,
    isLoadingSendOperationsFactor,
    errorSendOperationsFactor,
    sendOperationsFactor,
  ] = useSendOperationsFactor();
  const [
    passwordChangeResult,
    isLoadingPasswordChange,
    errorPasswordChange,
    submitPasswordChange,
  ] = usePasswordChange();

  const handleStartPasswordChange = async () => {
    try {
      await getOperationsFactor();
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (errorGetOperationsFactor) {
      setStep("error");
      setValidationError({
        generalError: errorGetOperationsFactor.message,
      });
    }
  }, [errorGetOperationsFactor]);

  useEffect(() => {
    if (!factor) {
      return;
    }

    if (
      factor.factorType !== "FACTOR_TYPE_PASSWORD" &&
      factor.factorType !== "FACTOR_TYPE_EMAIL_CODE" &&
      factor.factorType !== "FACTOR_TYPE_SMS_CODE"
    ) {
      setStep("error");
      setValidationError({
        generalError: "Sorry, we encountered an error. Please try again later",
      });
      console.log("Unexpected factor type", factor.factorType);
      return;
    }

    setStep("password_form");
  }, [factor]);

  useEffect(() => {
    if (passwordChangeResult) {
      setStep("success");
    }
  }, [passwordChangeResult]);

  useEffect(() => {
    if (errorPasswordChange) {
      setValidationError({
        generalError: errorPasswordChange.message,
      });
    }
  }, [errorPasswordChange]);

  useEffect(() => {
    if (sendOperationsFactorResult) {
      setSentCode(true);
    }
  }, [sendOperationsFactorResult]);

  useEffect(() => {
    if (errorSendOperationsFactor) {
      setValidationError({
        generalError: errorSendOperationsFactor.message,
      });
    }
  }, [errorSendOperationsFactor]);

  const handlePasswordSubmit = async () => {
    if (!isPasswordReady) {
      return;
    }

    if (factor?.factorType === "FACTOR_TYPE_PASSWORD") {
      try {
        await submitPasswordChange("", factor?.factorId, currentPass, newPass);
      } catch (e) { }

      return;
    }

    if (!sentCode) {
      try {
        await sendOperationsFactor();
      } catch (e) { }
      return;
    }

    if (!sendOperationsFactorResult) {
      console.log("No verification code sent, should not happen");
      return;
    }

    try {
      await submitPasswordChange(
        sendOperationsFactorResult.operationsSessionId,
        sendOperationsFactorResult.factor.factorId,
        verificationCode,
        newPass
      );
    } catch (e) {
      console.log(e);
    }
  };

  const onChangePass = (
    value: string,
    cb: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setValidationError({});
    cb(value);
  };

  useEffect(() => {
    if (step === "success" || step === "error") {
      setCurrentPass("");
      setNewPass("");
      setVerificationCode("");
      setSentCode(false);
    }
    if (step === "success") {
      setTimeout(() => {
        setStep("initial");
      }, 3000);
    }
  }, [step]);

  return (
    <Accordion disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: "800",
            fontSize: "20px",
            lineHeight: "28px",
            letterSpacing: "0.5px",
            color: "#1C1F21",
          }}
        >
          Change password
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {step === "initial" && (
          <Button
            onClick={handleStartPasswordChange}
            variant='contained'
            size='large'
            disabled={isLoadingOperationsFactor}
          >
            Change
          </Button>
        )}

        {step === "error" && (
          <>
            <Alert severity='error' sx={{ mb: 2 }}>
              {validationErrors.generalError}
            </Alert>
            <Button
              onClick={() => setStep("initial")}
              variant='outlined'
              size='large'
            >
              Try Again
            </Button>
          </>
        )}

        {step === "password_form" && (
          <>
            <PasswordChange
              askCurrentPassword={factor?.factorType === "FACTOR_TYPE_PASSWORD"}
              userEmail={userInfo?.email}
              onCurrentPasswordChange={setCurrentPass}
              onNewPasswordChange={setNewPass}
              onIsReadyChange={setIsPasswordReady}
            />

            {(factor?.factorType === "FACTOR_TYPE_EMAIL_CODE" ||
              factor?.factorType === "FACTOR_TYPE_SMS_CODE") &&
              sentCode && (
                <>
                  {sendOperationsFactorResult?.factor?.factorType ===
                    "FACTOR_TYPE_SMS_CODE" && (
                      <Typography variant='body1'>
                        {sendOperationsFactorResult?.factor.factorInfoMasked
                          ? `We sent a code via SMS to the phone number '${sendOperationsFactorResult?.factor.factorInfoMasked}'`
                          : "We sent a code via SMS to your phone number"}
                      </Typography>
                    )}
                  {sendOperationsFactorResult?.factor?.factorType ===
                    "FACTOR_TYPE_EMAIL_CODE" && (
                      <Typography variant='body1'>
                        {sendOperationsFactorResult?.factor.factorInfoMasked
                          ? `We sent a code to the email '${sendOperationsFactorResult?.factor.factorInfoMasked}'`
                          : "We sent a code to your email"}
                      </Typography>
                    )}
                  <TextField
                    fullWidth
                    value={verificationCode.trim()}
                    name='verificationCode'
                    label='Verification code'
                    error={!!validationErrors.codeError}
                    helperText={validationErrors.codeError}
                    inputProps={{
                      onChange: (e) =>
                        onChangePass(
                          (e.target as HTMLTextAreaElement).value,
                          setVerificationCode
                        ),
                    }}
                  />
                </>
              )}
            {validationErrors.generalError && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {validationErrors.generalError}
              </Alert>
            )}
            <Button
              onClick={handlePasswordSubmit}
              variant='contained'
              size='large'
              sx={{ marginTop: "16px" }}
              disabled={
                !isPasswordReady ||
                isLoadingPasswordChange ||
                isLoadingSendOperationsFactor
              }
            >
              Save changes
            </Button>
          </>
        )}

        {step === "success" && (
          <Alert severity='success' sx={{ mb: 2 }}>
            Password is successfully updated
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
